import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  CalendarEvent,
  CalendarEventType,
  CalendarEventsResponse,
} from "../types";

export interface UseCalendarEventsParams {
  dateFrom: string;
  dateTo: string;
  types: CalendarEventType[];
  companyId?: string;
  projectId?: string;
  assignedToMe: boolean;
}

const CALENDAR_KEY = ["calendar"] as const;

export function useCalendarEvents(
  params: UseCalendarEventsParams,
): UseQueryResult<CalendarEvent[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [
      ...CALENDAR_KEY,
      "events",
      params,
      accessToken ? "auth" : "anon",
    ],
    enabled:
      !!accessToken &&
      !!params.dateFrom &&
      !!params.dateTo &&
      params.types.length > 0,
    queryFn: async () => {
      const res = await apiFetch<CalendarEventsResponse>("/calendar/events", {
        token: accessToken,
        query: {
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
          types: params.types.join(","),
          companyId: params.companyId,
          projectId: params.projectId,
          assignedToMe: params.assignedToMe ? "true" : undefined,
        },
      });
      return res.events;
    },
    staleTime: 2 * 60 * 1000,
  });
}
